import React, { FunctionComponent, useCallback, useMemo } from "react";

import {
  TableCellProps,
  Td,
  Box,
  useColorModeValue,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";

import { JSONValue, SharedProps } from "./types";
import { format } from "d3";
import { useIntl } from "react-intl";


interface IProps extends TableCellProps, SharedProps {
  value: JSONValue;
  correction?: number;
  label: string;
  units?: string;
  isCurrentHour?: boolean;
  multiplier?: number;
  withoutValue?: boolean;
  signedValue?: boolean;
}


const RowValue: FunctionComponent<IProps> = ({
  value,
  correction = NaN,
  label,
  units = "",
  multiplier = 1,
  cellWidth,
  cellPaddingX,
  isCurrentHour,
  withoutValue = false,
  signedValue = false,
  ...rest
}) => {
  const intl = useIntl();
  const correctedValue = useMemo<number>(() => (isNaN(correction) ? value : correction) as number, [value, correction]);

  const trueMessage = useMemo(() => intl.formatMessage({
    id: "site__trading__row_value__true",
    defaultMessage: "Yes",
  }), [intl]);
  const falseMessage = useMemo(() => intl.formatMessage({
    id: "site__trading__row_value__false",
    defaultMessage: "No",
  }), [intl]);
  const insteadMessage = useMemo(() => intl.formatMessage({
    id: "site__trading__row_value__instead",
    defaultMessage: "instead",
  }), [intl]);

  const valueColor = useCallback((value: JSONValue): string | undefined => {
    if (typeof(value) === "number" && value * multiplier < 0) return "red.500";
  }, [multiplier]);

  const valueWeight = useCallback((value: JSONValue): string | undefined => {
    return isCurrentHour ? "bold" : "regular";
  }, [isCurrentHour]);

  const reasonablePrecision = useCallback((value: number): number | string => {
    const abs = Math.abs(Math.round(value));
    const sign = signedValue && value > 0 ? "+":""; // minus is already there

    if (value === 0 ) return value;
    else if (abs < 10) return `${sign}${format(".1f")(value)}`;
    else if (abs > 1000000) return `${sign}${reasonablePrecision(value / 1000000)}M`;
    else if (abs > 1000) return `${sign}${reasonablePrecision(value / 1000)}k`;
    else return `${sign}${Math.round(value)}`;
  }, [signedValue]);

  const formattedValue = useCallback((value: any):string => {
    switch(typeof(value)) {
      case "number":  return `${signedValue && value * multiplier > 0 ? "+":""}${(value * multiplier).toFixed(3)} ${units}`.trim();
      case "boolean": return `${value ? trueMessage : falseMessage}`;
      default:        return `${value} ${units}`.trim();
    }
  }, [multiplier, units, trueMessage, falseMessage, signedValue]);

  const tooltip = useCallback((): string => {
    if (value === null) return "";

    const lbl = label.replaceAll(/<[^>]+>/g, ""); // remove eventual HTML tags

    switch(typeof(value)) {
      case "boolean": return `${lbl}? ${formattedValue(value)}`;
      default:        return `${lbl}: ${isNaN(correction) || correction === value ? formattedValue(value) : `${formattedValue(correction)} ${insteadMessage} ${formattedValue(value)}`}`;
    }
  }, [label, value, correction, formattedValue, insteadMessage]);

  const pageTextColor = useColorModeValue("black", "white");
  const pageBackgroundColor = useColorModeValue("white", "gray.900");
  const booleanTrueColor = useColorModeValue("enposol.500", "enposol.500");
  const booleanFalseColor = useColorModeValue("gray.200", "gray.800");

  return (
    <>
      <Td
        title={tooltip()}
        textAlign="right"
        color={valueColor(correctedValue)}
        fontWeight={valueWeight(correctedValue)}
        fontSize={"xs"}
        px={`${cellPaddingX}px`}
        py={0}
        verticalAlign={typeof(value) == "boolean" ? "middle" : "top"}
        {...rest}
      >
        { typeof(value) == "boolean" && (
          <Box width={"100%"} height={2.5} borderTopLeftRadius={3} borderRightWidth={1} borderColor={pageBackgroundColor}
            bg={value ? booleanTrueColor : booleanFalseColor}
          ></Box>
        ) }
        { typeof(value) == "number" && (
          <Box width={cellWidth} whiteSpace="pre" overflow="hidden">
            { !withoutValue && <>
              { reasonablePrecision(correctedValue * multiplier) }
            </>}
            { withoutValue && <>
              { value * multiplier > 0 && <TriangleUpIcon color={pageTextColor}/> }
              { value * multiplier < 0 && <TriangleDownIcon color={pageTextColor}/> }
            </>}
          </Box>
        ) }
      </Td>
    </>
  )
}

export default RowValue;
